import { gql, type QueryHookOptions, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { Company, Query, QueryCompaniesWithFilterArgs } from '@/types/GraphqlSchema';
import type { ValuationsFilters as ValuationsFiltersType } from '../../store/valuationsStore';
import { AutoCompleteSimpleComposable } from './AutoCompleteSimpleComposable';
import { FilterControl } from './FilterControl';
import { FilterKey } from './filterConfig';

const COMPANIES_WITH_FILTER_QUERY = gql`
  query companiesWithFilter(
    $search: String
    $assets: [JSON!]
    $countries: [String!]
    $basins: [String!]
    $activeInBlocks: Boolean!
    $activeInLeases: Boolean!
    $includeHistoricalParticipants: Boolean!
    $nonOperator: Boolean!
    $operator: Boolean!
    $maBuyers: Boolean
    $maSellers: Boolean
    $isMAndAOpportunity: Boolean
    $pagination: PaginationInput
  ) {
    companiesWithFilter(
      search: $search
      assets: $assets
      countries: $countries
      basins: $basins
      activeInBlocks: $activeInBlocks
      activeInLeases: $activeInLeases
      includeHistoricalParticipants: $includeHistoricalParticipants
      nonOperator: $nonOperator
      operator: $operator
      maBuyers: $maBuyers
      maSellers: $maSellers
      isMAndAOpportunity: $isMAndAOpportunity
      pagination: $pagination
    ) {
      companies {
        id
        name
      }
      totalCount
    }
  }
`;

type CompaniesProps = {
  filters: ValuationsFiltersType;
  onChange: (companies: Company[]) => void;
  onControlChange: (value: string | boolean, key: string) => void;
  context: string;
};

type CompaniesQueryData = Pick<Query, 'companiesWithFilter'>;

const name = 'Company';
const note =
  '*Only the top 100 companies will be shown, you can access more companies by typing in the name.';

const controlItems = [
  {
    key: 'companyOperand',
    name: 'Filter Logic',
    label: 'Filter Type',
    type: 'radio' as const,
    tooltip: `AND: Returns results where the selected companies are ALL current participants
        OR: Returns results where any of the selected companies are current participants`,
    options: [
      {
        value: 'AND',
        label: 'AND',
      },
      {
        value: 'OR',
        label: 'OR',
      },
    ],
  },
  {
    key: 'operator',
    name: 'Operator',
    label: 'Operator',
    type: 'checkbox' as const,
    option: {
      name: 'Operator',
    },
  },
  {
    key: 'nonOperator',
    name: 'Non-Operator',
    label: 'Non-Operator',
    type: 'checkbox' as const,
    option: {
      name: 'Non-Operator',
    },
  },
  {
    key: 'includeHistoricalParticipants',
    name: 'Historical Participants',
    label: 'Historical Participants',
    type: 'checkbox' as const,
    option: {
      name: 'Historical Participants',
    },
  },
];

const buildQueryVariables = (
  filters: ValuationsFiltersType,
  searchValue: string,
): QueryCompaniesWithFilterArgs => ({
  search: searchValue,
  assets: filters[FilterKey.Assets] || [],
  countries: filters[FilterKey.Countries]?.map(country => country.isoCode) || [],
  basins: filters[FilterKey.Basins]?.map(basin => basin.name) || [],
  activeInBlocks: false,
  activeInLeases: false,
  includeHistoricalParticipants: Boolean(filters[FilterKey.IncludeHistoricalParticipants]),
  nonOperator: Boolean(filters[FilterKey.NonOperator]),
  operator: Boolean(filters[FilterKey.Operator]),
  isMAndAOpportunity: filters[FilterKey.Opportunities] || false,
});

export const CompaniesFilter = ({
  filters,
  onChange,
  onControlChange,
  context,
}: CompaniesProps) => {
  const [searchValue, setSearchValue] = useState('');

  const queryVariables = buildQueryVariables(filters, searchValue);

  const queryOptions: QueryHookOptions<CompaniesQueryData, QueryCompaniesWithFilterArgs> = {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  };

  const { data, loading } = useQuery<CompaniesQueryData, QueryCompaniesWithFilterArgs>(
    COMPANIES_WITH_FILTER_QUERY,
    queryOptions,
  );

  const companies = Array.isArray(data?.companiesWithFilter?.companies)
    ? data.companiesWithFilter.companies
    : [];

  const companiesValue = useMemo(() => {
    const filterCompanies = filters.companies;
    if (Array.isArray(filterCompanies)) {
      return filterCompanies;
    }
    if (filterCompanies && typeof filterCompanies === 'object') {
      return [filterCompanies];
    }
    return [];
  }, [filters.companies]);

  return (
    <AutoCompleteSimpleComposable<Company>
      options={companies}
      loading={loading}
      value={companiesValue}
      multiple={true}
      size='medium'
      getOptionLabel={option => (typeof option === 'string' ? option : option.name || '')}
      onChange={(_, newValue) => {
        onChange((newValue as Company[]) || []);
      }}
      onInputChange={(_, newInputValue) => {
        setSearchValue(newInputValue);
      }}
      renderInput={params => (
        <AutoCompleteSimpleComposable.StyledInput
          params={params}
          label={name}
          data-id='companies-filter-input'
        />
      )}
      renderTags={(value, getTagProps) => {
        const numTags = value.length;
        return (
          <>
            {value.slice(0, 1).map((option, index) => (
              <AutoCompleteSimpleComposable.StyledTag
                key={option.id || index}
                option={option}
                index={index}
                getTagProps={getTagProps}
                getOptionLabel={opt => opt.name || ''}
              />
            ))}
            {numTags > 1 && ` +${numTags - 1}`}
          </>
        );
      }}
      renderOption={(props, option, state) => {
        const isLastOption = companies.indexOf(option) === companies.length - 1;

        return (
          <React.Fragment key={option.id}>
            <AutoCompleteSimpleComposable.Option
              {...props}
              option={option}
              state={state}
              getOptionLabel={opt => opt.name || ''}
            />

            {isLastOption && (
              <Box sx={{ position: 'sticky', bottom: 0 }}>
                <FilterControl.Root
                  key='control'
                  controlValues={{
                    companyOperand: filters.companyOperand || 'OR',
                    operator: Boolean(filters.operator),
                    nonOperator: Boolean(filters.nonOperator),
                    includeHistoricalParticipants: Boolean(filters.includeHistoricalParticipants),
                  }}
                  onChange={onControlChange}
                  context={context}
                  dataId='companies-filter-control'
                >
                  <FilterControl.Container>
                    {controlItems.map(item => {
                      if (item.type === 'radio' && item.options) {
                        return (
                          <FilterControl.RadioGroup
                            key={item.key}
                            controlKey={item.key}
                            label={item.label}
                            ariaLabel={item.name}
                            options={item.options}
                            tooltip={item.tooltip}
                          />
                        );
                      }
                      return (
                        <FilterControl.Checkbox
                          key={item.key}
                          controlKey={item.key}
                          label={item.label}
                        />
                      );
                    })}
                  </FilterControl.Container>
                </FilterControl.Root>
                <AutoCompleteSimpleComposable.Note key='note' note={note} isHidden={false} />
              </Box>
            )}
          </React.Fragment>
        );
      }}
      data-id='companies-filter'
    />
  );
};
